import { supabase } from "@/integrations/supabase/client";
import type { 
  SharedLink, 
  SharedLinkWithAnalytics,
  ShareAnalytics 
} from "@/integrations/supabase/types";

export interface CreateSharedLinkParams {
  recordId: string;
  expiresAt?: string | null;
}

export interface CreateSharedLinkResponse {
  id: string;
  token: string;
  expiresAt: string | null;
  createdAt: string;
}

export interface SharingError {
  error: string;
  message: string;
}

/**
 * Создание shared ссылки для записи
 */
export const createSharedLink = async (
  params: CreateSharedLinkParams
): Promise<CreateSharedLinkResponse | SharingError> => {
  try {
    const { data, error } = await supabase.rpc('create_shared_link', {
      p_record_id: params.recordId,
      p_expires_at: params.expiresAt
    });

    if (error) {
      return {
        error: 'CREATION_FAILED',
        message: error.message
      };
    }

    if (!data || data.length === 0) {
      return {
        error: 'NO_DATA',
        message: 'Не удалось создать ссылку'
      };
    }

    const result = data[0];
    return {
      id: result.id,
      token: result.token,
      expiresAt: result.expires_at,
      createdAt: result.created_at
    };
  } catch (error) {
    console.error('Error creating shared link:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла неизвестная ошибка при создании ссылки'
    };
  }
};

/**
 * Получение всех shared ссылок пользователя для конкретной записи
 */
export const getRecordSharedLinks = async (
  recordId: string
): Promise<SharedLink[] | SharingError> => {
  try {
    const { data, error } = await supabase
      .from('shared_links')
      .select('*')
      .eq('record_id', recordId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      return {
        error: 'FETCH_FAILED',
        message: error.message
      };
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching shared links:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при получении ссылок'
    };
  }
};

/**
 * Получение всех shared ссылок пользователя
 */
export const getUserSharedLinks = async (): Promise<SharedLink[] | SharingError> => {
  try {
    const { data, error } = await supabase
      .from('shared_links')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      return {
        error: 'FETCH_FAILED',
        message: error.message
      };
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching user shared links:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при получении ссылок пользователя'
    };
  }
};

/**
 * Деактивация shared ссылки
 */
export const deactivateSharedLink = async (
  sharedLinkId: string
): Promise<boolean | SharingError> => {
  try {
    const { data, error } = await supabase.rpc('deactivate_shared_link', {
      p_shared_link_id: sharedLinkId
    });

    if (error) {
      return {
        error: 'DEACTIVATION_FAILED',
        message: error.message
      };
    }

    return data === true;
  } catch (error) {
    console.error('Error deactivating shared link:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при деактивации ссылки'
    };
  }
};

/**
 * Реактивация shared ссылки
 */
export const reactivateSharedLink = async (
  sharedLinkId: string
): Promise<boolean | SharingError> => {
  try {
    const { data, error } = await supabase.rpc('reactivate_shared_link', {
      p_shared_link_id: sharedLinkId
    });

    if (error) {
      return {
        error: 'REACTIVATION_FAILED',
        message: error.message
      };
    }

    return data === true;
  } catch (error) {
    console.error('Error reactivating shared link:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при реактивации ссылки'
    };
  }
};

/**
 * Обновление даты истечения shared ссылки
 */
export const updateSharedLinkExpiration = async (
  sharedLinkId: string,
  expiresAt: string | null
): Promise<SharedLink | SharingError> => {
  try {
    const { data, error } = await supabase
      .from('shared_links')
      .update({ 
        expires_at: expiresAt,
        updated_at: new Date().toISOString()
      })
      .eq('id', sharedLinkId)
      .select()
      .single();

    if (error) {
      return {
        error: 'UPDATE_FAILED',
        message: error.message
      };
    }

    return data;
  } catch (error) {
    console.error('Error updating shared link expiration:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при обновлении ссылки'
    };
  }
};

/**
 * Получение аналитики для shared ссылки
 */
export const getSharedLinkAnalytics = async (
  sharedLinkId: string
): Promise<SharedLinkWithAnalytics | SharingError> => {
  try {
    // Получаем саму ссылку
    const { data: linkData, error: linkError } = await supabase
      .from('shared_links')
      .select('*')
      .eq('id', sharedLinkId)
      .single();

    if (linkError) {
      return {
        error: 'LINK_NOT_FOUND',
        message: linkError.message
      };
    }

    // Получаем аналитику
    const { data: analyticsData, error: analyticsError } = await supabase
      .from('share_analytics')
      .select('*')
      .eq('shared_link_id', sharedLinkId)
      .order('viewed_at', { ascending: false });

    if (analyticsError) {
      return {
        error: 'ANALYTICS_FETCH_FAILED',
        message: analyticsError.message
      };
    }

    return {
      ...linkData,
      analytics: analyticsData || [],
      total_views: analyticsData?.length || 0
    };
  } catch (error) {
    console.error('Error fetching shared link analytics:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при получении аналитики'
    };
  }
};

/**
 * Проверка доступности записи для шаринга (только completed записи)
 */
export const canShareRecord = async (recordId: string): Promise<boolean | SharingError> => {
  try {
    const { data, error } = await supabase
      .from('records')
      .select('status, processing_status')
      .eq('id', recordId)
      .single();

    if (error) {
      return {
        error: 'RECORD_CHECK_FAILED',
        message: error.message
      };
    }

    // Запись может быть поделена если processing_status или status равен 'completed'
    return data?.processing_status === 'completed' || data?.status === 'completed';
  } catch (error) {
    console.error('Error checking record share eligibility:', error);
    return {
      error: 'UNKNOWN_ERROR',
      message: 'Произошла ошибка при проверке записи'
    };
  }
};

/**
 * Генерация полного URL для shared ссылки
 */
export const generateShareUrl = (token: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/shared/${token}`;
};

/**
 * Копирование ссылки в буфер обмена
 */
export const copyShareUrlToClipboard = async (token: string): Promise<boolean> => {
  try {
    const shareUrl = generateShareUrl(token);
    await navigator.clipboard.writeText(shareUrl);
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};